/* eslint-disable import/no-unresolved */
import { Dispatch, useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { isEmpty } from 'lodash-es';
import { Action } from 'redux';

import { PredefinedMilisecondsTypeEnum } from './lib/enums';
import lang from './lib/language';
import { IGNORE_GATEWAY_CONNECTION } from './lib/libSettings';
import { ENROLL_AUTH_REQUEST_TYPE } from './lib/models/enroll-requests';
import { RootState } from './lib/store';
import { getAllMessages } from './lib/store/ams';
import { appConnected as appConnectedCall, setOnlineStatus, setWebLoginReady } from './lib/store/app';
import {
  applicationStartup,
  gatewayConnected as gatewayConnectedCall,
  getIndividualExtendedInfo,
  login,
  logout,
  reconnectEventAction,
} from './lib/store/common-actions';
import { getDefaultSelectableTradingPLanFromState } from './lib/store/crm/helpers';
import { CRMCall } from './lib/store/crm/types';
import {
  getCurrentAccount,
  hasValidCookie,
  isAuthEnrollPortalStatusInitial,
  isAuthEnrollPortalStatusPending,
  isCookieCheckPending,
  isCRMEnrollReady,
  isLoginPending as storeIsLoginPending,
  isUserVerifiedStatus,
} from './lib/store/selectors';
import GatewayWebsocketState from './lib/store-util/GatewayWebsocketState';
import { isDateAfterNow, isDateBeforeNow } from './lib/util/DateTimeHelpers';
import { isCallStatusReady } from './lib/util/error-handling/StatusByCallHelpers';

import TransactionHistory from './containers/Funds/transactions/TransactionHistory/TransactionHistory';
import ChangePasswordConfirmation from './containers/MyProfile/ChangePassword/ChangePasswordConfirmation';
import { startMockedMode } from './util/helpers';
import { UI_ROUTE_MOCKED, UI_ROUTES } from './util/routes';
import {
  ConnectionStatusBar,
  Grid,
  MyAccountDocuments,
  MyAccountNotifications,
  myAccountPersonalDetails as MyAccountPersonalDetails,
  Preloader,
  ScrollToTop,
  SearchSymbolsMob,
  TournamentEndTimer,
  TournamentStartTimer,
} from './components';
import { MOCK, setLogConfig } from './configDebug';
import {
  BalancesheetStatement,
  BuyAndSell,
  CashFlowStatement,
  Discover,
  DividendPayersAndHighGrowthCompaniesContainer,
  EarningsCalendarAllItems,
  EnrollAccountActivated,
  EnrollAuthInfo,
  EnrollPersonalInfo,
  EnrollSetupUserName,
  EnrollVerifyEmail,
  EpicMoversInternal,
  Error,
  FiftyTwoWeekHighLow,
  IncomeStatement,
  Login,
  Modify,
  MyAccount,
  MyPosition,
  MyProfile,
  Overview,
  PopularGroupTable,
  SymbolDetails,
  TopGainersAndLosersList,
  TradeHistory,
  TradingViewChart,
  VerificationLanding,
  VerificationPhoneNumber,
  VerificationPhoneOTP,
  VerificationPoA,
  VerificationPoI,
  VerificationTermsConditions,
} from './containers';
import HeaderAndSideBar from './HeaderAndSideBar';
import { useIsMobile } from './hooks';
import useNetwork from './useNetwork';

import styles from './App.module.scss';

type Props = ReduxProps;

const App = (props: Props): JSX.Element => {
  const ref = useRef({ appLoaded: false, path: '' });

  const {
    start,
    isOnline,
    isForceRedirectToEnroll,
    isAuthenticated,
    isAppLoaded,
    gatewayConnected,
    appConnected,
    isEnrollPortalStatusInitial,
    isEnrollPortalStatusPending,
    isGatewayConnectionTimedOut,
    isCookieValid,
    isPhoneConfirmed,
    isProfileNameCreated,
    loginResultCallback,
    setLoginReady,
    isExtendedInfoReady,
    isLoginPending,
    isAppConnected,
    currentAccount,
    isUserVerified,
    getAllDataMessages,
    appLogOut,
    setNetworkStatus,
    reconnectToGwAndSubscibeSymbols,
    loginRedirect,
    getExtendedInfo,
    phoneNumber,
    phoneCode,
    isTournamentEnding,
    isTradingPlanReady,
    isCookieSessionCheckPending,
    isRedeemSignupTokenFlow,
  } = props;

  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search, state } = location;

  const networkStatus = useNetwork();
  const { isConnected: isGWConnected, isLoggedin: isGWLoggedin } = GatewayWebsocketState.use();

  useEffect(() => {
    if (isGWConnected && isGWLoggedin) {
      gatewayConnected();
      appConnected();
    }
  }, [isGWConnected, isGWLoggedin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const metaTag = document.querySelector('meta[name="viewport"]');

    if (isMobile && metaTag) {
      metaTag.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    }

    return () => {
      if (metaTag) {
        metaTag.setAttribute('content', 'width=device-width, initial-scale=1');
      }
    };
  }, [isMobile]);

  useEffect(() => {
    if (isAuthenticated) {
      getExtendedInfo();
    }
  }, [isAuthenticated, getExtendedInfo]);

  useEffect(() => {
    if (networkStatus && !isOnline) {
      reconnectToGwAndSubscibeSymbols();
    }
    setNetworkStatus(networkStatus);
  }, [isOnline, networkStatus, reconnectToGwAndSubscibeSymbols, setNetworkStatus]);

  const [isRedirected, setRedirected] = useState<boolean>(false);
  const [isLoadedApp, setIsLoadedApp] = useState<boolean>(false);
  const [isRedirectLogin, setIsRedirectLogin] = useState<boolean>(false);

  const searchParams = new URLSearchParams(window.location.search);
  const isDirectLogin = searchParams.get('directLogin');
  if (isDirectLogin && !isRedirectLogin) {
    setIsRedirectLogin(true);
  }

  useEffect(() => {
    if (isRedirectLogin && !isAuthenticated && isAppLoaded) {
      loginRedirect();
    }
  }, [isRedirectLogin, isAppLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const isErrorPath = location.pathname.includes(UI_ROUTES.error);

  const isEnrollPath = ([
    UI_ROUTES.accountActivated,
    UI_ROUTES.enrollPhoneVerificaion,
    UI_ROUTES.enrollPhoneVerificaionOtp,
    UI_ROUTES.enrollSetupUserName,
  ] as string[]).includes(location.pathname) || (isAuthenticated && isExtendedInfoReady && !isPhoneConfirmed)
  || (isAuthenticated && isExtendedInfoReady && !isProfileNameCreated);

  const isFundsPath = [
    UI_ROUTES.funds + UI_ROUTES.withdrawalAmount,
    UI_ROUTES.funds + UI_ROUTES.addYourBankAccount,
    UI_ROUTES.funds + UI_ROUTES.uploadBankStatement,
    UI_ROUTES.funds + UI_ROUTES.enterAmount,
    UI_ROUTES.funds + UI_ROUTES.depositSlip,
    UI_ROUTES.funds + UI_ROUTES.makeYourFirstDeposit,
  ].includes(location.pathname);

  const isVerificationPath = [
    UI_ROUTES.verification,
    UI_ROUTES.verification + UI_ROUTES.verificationPhoneNumber,
    UI_ROUTES.verification + UI_ROUTES.verificationPhoneOTP,
    UI_ROUTES.verification + UI_ROUTES.verificationPoI,
    UI_ROUTES.verification + UI_ROUTES.verificationPoA,
    UI_ROUTES.verification + UI_ROUTES.verificationTermsAndConditions,
  ].includes(location.pathname);

  const isAuthPath = [
    UI_ROUTES.signUp as string,
    UI_ROUTES.authResult as string,
    UI_ROUTES.emailConfirm as string,
    '/',
  ].includes(location.pathname);

  const [isExtraLoader, setIsExtraLoader] = useState<boolean>(true);

  useEffect(() => {
    if (!isLoadedApp) {
      return;
    }

    if (isEnrollPath || isVerificationPath || isAuthPath || !isAuthenticated) {
      setIsExtraLoader(false);
      return;
    }

    setIsExtraLoader(true);
    const timeout = setTimeout(() => {
      setIsExtraLoader(false);
    }, PredefinedMilisecondsTypeEnum.twoSeconds);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeout);
  }, [
    isLoadedApp,
    isEnrollPath,
    isVerificationPath,
    isAuthPath,
    isAuthenticated,
  ]);

  const isBuyOrSellPath = location.pathname.includes(UI_ROUTES.buy) || location.pathname.includes(UI_ROUTES.sell);
  const isModifyPath = location.pathname.includes(UI_ROUTES.modify);
  const isAuthResultPath = location.pathname === UI_ROUTES.authResult;
  const tradeHistoryPath = location.pathname === UI_ROUTES.tradeHistory;
  const tansactionHistoryPath = location.pathname === UI_ROUTES.transactionHistory;
  const searchSymbolPathMob = location.pathname === UI_ROUTES.searchSymbolsMob;
  const isMockedPath = pathname.includes(UI_ROUTE_MOCKED);

  const visibleHeaderAndSidebar = isMobile
    ? (
      !isEnrollPath
      && !isExtraLoader
      && !isVerificationPath
      && !isFundsPath
      && (!isAuthPath)
      && !isBuyOrSellPath
      && !isModifyPath
      && !tradeHistoryPath
      && !tansactionHistoryPath
      && !searchSymbolPathMob
    ) : (
      !isEnrollPath
      && !isExtraLoader
      && !isVerificationPath
      && (!isAuthPath)
    );

  const emailConfirmPath = [
    UI_ROUTES.emailConfirm as string,
    UI_ROUTES.emailResend as string,
  ].includes(pathname);
  const emailConfirmationOrResentWhileLoggedIn = (emailConfirmPath && isCookieValid && !isRedeemSignupTokenFlow);

  // did mount
  useEffect(() => {
    if (ref.current.appLoaded) return;
    ref.current.appLoaded = true;

    if (emailConfirmationOrResentWhileLoggedIn) {
      appLogOut({ preserveEnrollUserId: true });
      return;
    }
    if (pathname.includes(UI_ROUTE_MOCKED) && !MOCK.ENABLED) {
      startMockedMode();
      navigate(pathname, {
        replace: true,
      });
    } else {
      start();
      getAllDataMessages();
    }

    if (search.includes('debug=')) {
      const queryParams = new URLSearchParams(search);
      setLogConfig(queryParams.get('debug'));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isGWLoggedin && !IGNORE_GATEWAY_CONNECTION) {
      setRedirected(false);
    }
  }, [isGWLoggedin]);

  /**
   * The following two useEffect are for handling auth-result
   * */
  useEffect(() => {
    if (isAppLoaded && isAuthResultPath) {
      loginResultCallback();
    }
  }, [loginResultCallback, isAppLoaded, isAuthResultPath]);

  const hasSession = location.search.includes('session_state=');

  useEffect(() => {
    if (
      MOCK.ENABLED
      && !pathname.includes(UI_ROUTE_MOCKED)
      && pathname.match(/[/](overview|symbol|discover|payment|reports|trade-history|my-account)/i)
      && pathname !== ref.current.path
    ) {
      ref.current.path = pathname;
      navigate(pathname + UI_ROUTE_MOCKED, {
        replace: true,
      });
    }

    if (!ref.current.appLoaded) return;

    // normal login
    if (
      isAuthenticated
      && isForceRedirectToEnroll
      && ![
        UI_ROUTES.enrollPhoneVerificaion,
        UI_ROUTES.enrollPhoneVerificaionOtp,
        UI_ROUTES.enrollSetupUserName,
      ].includes(pathname as UI_ROUTES)
    ) {
      setLoginReady();
      navigate(UI_ROUTES.enrollPhoneVerificaion);
    } else if (isAuthenticated && isPhoneConfirmed !== undefined && !isPhoneConfirmed && !isProfileNameCreated) {
      if (!(phoneCode && phoneNumber && isPhoneConfirmed !== undefined) && !isProfileNameCreated) {
        setLoginReady();
        navigate(UI_ROUTES.enrollPhoneVerificaion);
      }
    } else if (isAuthenticated && !isProfileNameCreated && isPhoneConfirmed) {
      setLoginReady();
      navigate(UI_ROUTES.enrollSetupUserName);
    } else if (isAuthResultPath && isCookieValid
      && hasSession && !isRedirected && isPhoneConfirmed && isProfileNameCreated) {
      setLoginReady();
      navigate(UI_ROUTES.overview);
    } else if (!isAuthResultPath && isCookieValid && !isRedirected && isAppLoaded) {
      setRedirected(true);
      setLoginReady();
    }
  }, [// eslint-disable-line react-hooks/exhaustive-deps
    isExtendedInfoReady,
    isPhoneConfirmed,
    isProfileNameCreated,
    isAuthResultPath,
    isCookieValid,
    hasSession,
    setLoginReady,
    navigate,
    isAppLoaded,
    isAuthenticated,
    isRedirected,
    isForceRedirectToEnroll,
    pathname,
    search,
    isEnrollPortalStatusInitial,
    isEnrollPortalStatusPending,
  ]);

  useEffect(() => {
    if (
      isLoadedApp
      && pathname !== UI_ROUTES.emailConfirm
      && pathname !== UI_ROUTES.emailResend
      && pathname !== UI_ROUTES.signUp
      && pathname !== UI_ROUTES.baseURL
      && (!state && pathname === UI_ROUTES.overview)
    ) {
      if (isAuthenticated && isExtendedInfoReady && !phoneCode && !phoneNumber && !isProfileNameCreated) {
        if (!isPhoneConfirmed && !isProfileNameCreated) {
          navigate(UI_ROUTES.enrollPhoneVerificaion);
        } else if (isPhoneConfirmed && !isProfileNameCreated) {
          navigate(UI_ROUTES.enrollSetupUserName);
        } else {
          navigate(UI_ROUTES.overview);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isExtendedInfoReady,
    isPhoneConfirmed,
    isProfileNameCreated,
    isLoadedApp,
    isEnrollPortalStatusInitial,
    isEnrollPortalStatusPending,
  ]);

  let loaderMessage = '';
  if (
    isAuthResultPath
    || isCookieSessionCheckPending
    || isLoginPending
    || (isCookieValid && !isAppConnected && !isForceRedirectToEnroll)
    || (isGatewayConnectionTimedOut && !IGNORE_GATEWAY_CONNECTION)
    || emailConfirmationOrResentWhileLoggedIn
    || (MOCK.ENABLED && !isGWLoggedin)
    || (!MOCK.ENABLED && isMockedPath)
    || (!currentAccount && !isForceRedirectToEnroll && isUserVerified)
    || (isAuthenticated && !isForceRedirectToEnroll && !isExtendedInfoReady)
    || (isAuthenticated && !isForceRedirectToEnroll && isPhoneConfirmed === undefined)
  ) {
    loaderMessage = isLoginPending ? lang.commonMessageLoading() : lang.commonMessageConnecting();
    if (isGatewayConnectionTimedOut) loaderMessage = lang.commonMessageConnectionTimedOut();
    if (pathname.match(/tradingView/i)) {
      return (
        <div className={styles.loadingIndicator}>
          <div className={styles.spinner} />
        </div>
      );
    }
    return (
      <div className={styles.loader}>
        <Preloader
          isFullScreen
          text={loaderMessage}
          timeOutSetup={{
            checkIfTimeout: () => !isCookieValid && !isCookieSessionCheckPending,
            onTimeout: () => {
              appLogOut();
              navigate(UI_ROUTES.signUp);
            },
          }}
          owner="App"
        />
      </div>
    );
  }

  if (!isLoadedApp) {
    setIsLoadedApp(true);
  }

  return (
    <div
      className={[
        !pathname.startsWith(UI_ROUTES.tradingView) && styles.container,
        isEnrollPath && styles.enrollContainer,
        isVerificationPath && styles.enrollContainer,
        isFundsPath && styles.enrollContainer,
        isAuthPath && styles.enrollContainer,
        isErrorPath && styles.enrollContainer,
      ].join(' ')}
    >
      {(isAuthenticated
        && !pathname.startsWith(UI_ROUTES.error)
        && !pathname.startsWith(UI_ROUTES.tradingView)
      ) && (
      <>
        {visibleHeaderAndSidebar && <HeaderAndSideBar />}
        <ToastContainer />
        <ConnectionStatusBar />
      </>
      )}

      {/* eslint-disable-next-line no-nested-ternary */}
      <div className={isEnrollPath ? undefined : (pathname.startsWith(UI_ROUTES.tradingView) ? styles.whiteBody : (
        isEnrollPath
        || isVerificationPath
        || isFundsPath
        || isAuthPath
        || isErrorPath) ? styles.enrollContent : styles.content)}
      >

        <div
          className={[
            styles.mainContent,
            // eslint-disable-next-line no-nested-ternary
            pathname.startsWith(UI_ROUTES.tradingView) ? null
              : (isMobile ? styles.contentWrapperMobile : styles.contentWrapper),
            isEnrollPath && styles.enrollContentWrapper,
            isVerificationPath && styles.enrollContentWrapper,
            isFundsPath && styles.enrollContentWrapper,
            isAuthPath && styles.enrollContentWrapper,
          ].join(' ')}
        >
          {
            isAuthenticated ? (
              <>
                {isExtendedInfoReady
                  && isPhoneConfirmed
                  && isProfileNameCreated
                  && isTradingPlanReady
                && (
                <div style={{ display: isExtraLoader ? 'none' : 'block' }}>
                  <Grid>
                    {isTournamentEnding ? <TournamentEndTimer /> : <TournamentStartTimer />}
                  </Grid>
                </div>
                )}
                {
                  isExtraLoader ? (
                    <div className={styles.loader}>
                      <Preloader
                        isFullScreen
                        text={!isEmpty(loaderMessage) ? loaderMessage : lang.commonMessageConnecting()}
                        owner="App"
                      />
                    </div>
                  ) : (
                    <>
                      <ScrollToTop />
                      <Routes>
                        <Route
                          element={<TradingViewChart />}
                          path={`${UI_ROUTES.tradingView}/:name`}
                        />
                        <Route path={UI_ROUTES.error} element={<Error />} />
                        <Route path={UI_ROUTES.myAccount} element={<MyAccount />} />
                        <Route path={UI_ROUTES.myAccount + UI_ROUTE_MOCKED} element={<MyAccount />} />
                        <Route path={UI_ROUTES.myAccountNotifications} element={<MyAccountNotifications />} />
                        <Route path={UI_ROUTES.myAccountDocuments} element={<MyAccountDocuments />} />
                        <Route path={UI_ROUTES.myAccountPersonalDetails} element={<MyAccountPersonalDetails />} />
                        <Route path={`${UI_ROUTES.overview}/*`} element={<Overview selectedSymbol="" />} />
                        <Route path={UI_ROUTES.discover} element={<Discover />} />
                        <Route path={UI_ROUTES.myPosition} element={<MyPosition />} />
                        <Route path={UI_ROUTES.discover + UI_ROUTE_MOCKED} element={<Discover />} />
                        <Route
                          path={`${UI_ROUTES.symbol}/:name${UI_ROUTES.cashFlowStatement}`}
                          element={<CashFlowStatement />}
                        />
                        <Route path={`${UI_ROUTES.symbol}/:name${UI_ROUTES.incomeStatement}`} element={<IncomeStatement />} />
                        <Route
                          path={`${UI_ROUTES.symbol}/:name${UI_ROUTES.balancesheetStatement}`}
                          element={<BalancesheetStatement />}
                        />
                        <Route path={`${UI_ROUTES.symbol}/:name`} element={<SymbolDetails />} />
                        <Route path={`${UI_ROUTES.buy}/:name`} element={<BuyAndSell />} />
                        <Route path={`${UI_ROUTES.sell}/:name`} element={<BuyAndSell />} />
                        <Route path={UI_ROUTES.searchSymbolsMob} element={<SearchSymbolsMob />} />
                        <Route path={UI_ROUTES.enrollPhoneVerificaion} element={<VerificationPhoneNumber />} />
                        <Route path={UI_ROUTES.enrollPhoneVerificaionOtp} element={<VerificationPhoneOTP />} />
                        <Route path={UI_ROUTES.enrollSetupUserName} element={<EnrollSetupUserName />} />
                        <Route
                          path={UI_ROUTES.discover + UI_ROUTES.topMovers}
                          element={<TopGainersAndLosersList />}
                        />
                        <Route
                          element={<Modify />}
                          path={UI_ROUTES.modify}
                        />
                        <Route
                          path={UI_ROUTES.discover + UI_ROUTES.popularGroup}
                          element={<PopularGroupTable />}
                        />
                        <Route
                          path={UI_ROUTES.discover + UI_ROUTES.fiftyTwoWeekHighLow}
                          element={<FiftyTwoWeekHighLow />}
                        />
                        {/* TODO: Enable New on the Market feature when the backend team fiexes the konown issues */}
                        <Route
                          path={UI_ROUTES.discover + UI_ROUTES.earningsCalendar}
                          element={<EarningsCalendarAllItems />}
                        />
                        <Route
                          path={UI_ROUTES.discover + UI_ROUTES.epicMoversToday}
                          element={<EpicMoversInternal />}
                        />
                        <Route path={UI_ROUTES.tradeHistory} element={<TradeHistory />} />
                        <Route path={UI_ROUTES.transactionHistory} element={<TransactionHistory />} />
                        <Route path={UI_ROUTES.myProfile} element={<MyProfile />} />
                        <Route
                          path={UI_ROUTES.verification}
                          element={<VerificationLanding />}
                        />
                        <Route
                          path={UI_ROUTES.accountActivated}
                          element={<EnrollAccountActivated />}
                        />
                        <Route
                          element={<VerificationPoI />}
                          path={UI_ROUTES.verification + UI_ROUTES.verificationPoI}
                        />
                        <Route
                          element={<VerificationPoA />}
                          path={UI_ROUTES.verification + UI_ROUTES.verificationPoA}
                        />
                        <Route
                          element={<VerificationTermsConditions />}
                          path={UI_ROUTES.verification + UI_ROUTES.verificationTermsAndConditions}
                        />
                        <Route
                          element={<DividendPayersAndHighGrowthCompaniesContainer />}
                          path={UI_ROUTES.discover + UI_ROUTES.dividendPayersAndHighGrowthCompaniesPath}
                        />
                        <Route element={<ChangePasswordConfirmation />} path={UI_ROUTES.myAccountChangePassword} />
                        <Route path="*" element={<Navigate to={UI_ROUTES.overview} replace />} />
                      </Routes>
                    </>
                  )
                }
              </>
            ) : (
              <Routes>
                <Route path="/" element={<Login />} />
                <Route path={UI_ROUTES.error} element={<Error />} />
                <Route path={UI_ROUTES.firstEnroll} element={<EnrollPersonalInfo />} />
                <Route path={UI_ROUTES.secondEnroll} element={<EnrollAuthInfo />} />
                <Route path={UI_ROUTES.emailConfirm} element={<EnrollVerifyEmail />} />
                <Route path={UI_ROUTES.enrollPhoneVerificaion} element={<VerificationPhoneNumber />} />
                <Route path={UI_ROUTES.enrollPhoneVerificaionOtp} element={<VerificationPhoneOTP />} />
                <Route path={UI_ROUTES.enrollSetupUserName} element={<EnrollSetupUserName />} />
                <Route path={UI_ROUTES.signUp} element={<Login />} />
                <Route path={UI_ROUTES.emailResend} element={<EnrollVerifyEmail />} />
                <Route path="*" element={<Navigate to={UI_ROUTES.signUp} replace />} />
              </Routes>
            )
          }
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isForceRedirectToEnroll: state.auth.isForceRedirectToEnroll,
  isAppLoaded: state.app.loaded,
  isOnline: state.app.isOnline,
  isAppConnected: state.app.connected,
  isLoginPending: storeIsLoginPending(state),
  isWebLoginReady: state.app.isWebLoginReady,
  isGatewayConnectionTimedOut: state.gateway.status === 'timedout',
  isCookieValid: hasValidCookie(state),
  isCookieSessionCheckPending: isCookieCheckPending(state),
  isAuthenticated: hasValidCookie(state) && state.app.loaded,
  isPhoneConfirmed: state.crm.individualExtendedInfo?.phone_number_confirmed,
  isProfileNameCreated: !!(state.crm.individualExtendedInfo?.profile_name),
  currentAccount: getCurrentAccount(state),
  isUserVerified: isUserVerifiedStatus(state.crm.individualExtendedInfo),
  isCRMEnrollReadyStatus: isCRMEnrollReady(state),
  isEnrollPortalStatusInitial: isAuthEnrollPortalStatusInitial(state),
  isEnrollPortalStatusPending: isAuthEnrollPortalStatusPending(state),
  isExtendedInfoReady: isCallStatusReady(CRMCall.getIndividualExtendedInfo, state.crm.statusByCall),
  isTradingPlanReady: isCallStatusReady(CRMCall.searchTradingPlans, state.crm.statusByCall),
  isRedeemSignupTokenFlow: state.auth.enroll.lastType === ENROLL_AUTH_REQUEST_TYPE.REDEEM_SIGNUP_TOKEN,
  isTournamentEnding: isDateBeforeNow(getDefaultSelectableTradingPLanFromState(state.crm).plan_purchase_period_end)
    && isDateAfterNow(getDefaultSelectableTradingPLanFromState(state.crm).plans_accounts_deactivation),
  phoneNumber: state.auth.enroll.phoneNumber,
  phoneCode: state.auth.enroll.phoneCode,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  loginRedirect: () => {
    dispatch(login('login'));
  },
  start: () => dispatch(applicationStartup()),
  gatewayConnected: () => dispatch(gatewayConnectedCall()),
  appConnected: () => dispatch(appConnectedCall()),
  appLogOut: (payload?: Parameters<typeof logout>[0]) => dispatch(logout(payload)),
  loginResultCallback: () => dispatch(login('callback')),
  setLoginReady: () => dispatch(setWebLoginReady()),
  reconnectToGwAndSubscibeSymbols: () => dispatch(reconnectEventAction('App')),
  getAllDataMessages: () => dispatch(getAllMessages()),
  setNetworkStatus: (hasConnection: boolean) => dispatch(setOnlineStatus(hasConnection)),
  getExtendedInfo: (): void => {
    dispatch(getIndividualExtendedInfo());
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(App);
